import { useState, useCallback, useEffect } from "react";
import { isEqual } from "lodash";
import { AgGrid, AgGridReact, toast } from "@jsq/ds/core";
import {
  useCreateBillingUserGridViewMutation,
  useGetBillingUserGridViewQuery,
} from "~/graphql/supergraphHooks";
import {
  type GetBillingUserGridViewQuery,
  type BillingUserGridName,
} from "~/graphql/supergraphTypes";
import { applyFiltersToGrid } from "../utils/agGridUtils";

type ColumnDefs = AgGridReact.AgGridReactProps["columnDefs"];

type SavableGridConfiguration = Pick<
  AgGrid.GridState,
  "columnVisibility" | "columnSizing" | "columnOrder" | "filter"
>;

interface UseGridViewManagerProps {
  gridName: BillingUserGridName;
  defaultColumnDefs: ColumnDefs;
  gridApi?: AgGrid.GridApi;
  isBillingSaveUserGridViewFlagEnabled: boolean;
}

interface UseGridViewManagerReturn {
  columnDefs: ColumnDefs | null;
  shouldShowSaveViewButton: boolean;
  shouldShowClearFilters: boolean;
  shouldShowGrid: boolean;
  saveViewLoading: boolean;
  handleSavedViewClicked: () => Promise<void>;
  handleClearFiltersClicked: () => void;
  handleOnColumnMoved: (event: AgGrid.ColumnMovedEvent) => void;
  handleOnColumnVisible: (event: AgGrid.ColumnVisibleEvent) => void;
  handleOnColumnResized: (event: AgGrid.ColumnResizedEvent) => void;
  handleOnFilterChanged: (event: AgGrid.FilterChangedEvent) => void;
}

export const useGridViewManager = ({
  gridName,
  defaultColumnDefs,
  gridApi,
  isBillingSaveUserGridViewFlagEnabled,
}: UseGridViewManagerProps): UseGridViewManagerReturn => {
  const [shouldShowSaveViewButton, setShouldShowSaveViewButton] = useState(false);
  const [shouldShowClearFilters, setShouldShowClearFilters] = useState(false);
  const [shouldShowGrid, setShouldShowGrid] = useState(false);
  const [columnDefs, setColumnDefs] = useState<ColumnDefs | null>(null);
  const [savedFilterModelFromApi, setSavedFilterModelFromApi] =
    useState<AgGrid.FilterModel | null>(null);
  // Store the last saved filter model to compare against current AgGrid state(filter)
  const [lastSavedFilterModel, setLastSavedFilterModel] = useState<AgGrid.FilterModel | null>(
    null,
  );

  const handleGetBillingUserGridViewCompleted = (data: GetBillingUserGridViewQuery) => {
    let newColumnDefs: ColumnDefs = defaultColumnDefs;

    if (data?.getBillingUserGridView?.configuration) {
      const configuration = data?.getBillingUserGridView
        ?.configuration as SavableGridConfiguration;

      if (configuration && defaultColumnDefs) {
        const { columnVisibility, columnOrder, columnSizing, filter } = configuration;

        newColumnDefs = defaultColumnDefs.map((colDef) => {
          const updatedColDef = { ...colDef };
          const colId =
            ("colId" in colDef && colDef.colId) || ("field" in colDef && colDef.field);
          if (colId) {
            if ("hide" in updatedColDef && columnVisibility?.hiddenColIds) {
              updatedColDef.hide = columnVisibility?.hiddenColIds?.includes(colId);
            }
            // Apply saved column width directly to columnDef
            const savedWidth = columnSizing?.columnSizingModel?.find(
              (col: { colId: string; width?: number }) => col.colId === colId,
            )?.width;
            if (savedWidth && "width" in updatedColDef) {
              updatedColDef.width = savedWidth;
            }
          }
          return updatedColDef;
        });

        if (columnOrder?.orderedColIds) {
          const orderedColumnDefs: ColumnDefs = [];
          const columnDefMap = new Map<string, AgGrid.ColDef | AgGrid.ColGroupDef>();

          newColumnDefs.forEach((colDef) => {
            const colId =
              ("colId" in colDef && colDef.colId) || ("field" in colDef && colDef.field);
            if (colId) {
              columnDefMap.set(colId, colDef);
            }
          });

          columnOrder.orderedColIds.forEach((colId: string) => {
            const colDef = columnDefMap.get(colId);
            if (colDef) {
              orderedColumnDefs.push(colDef);
              columnDefMap.delete(colId);
            }
          });

          columnDefMap.forEach((colDef) => {
            orderedColumnDefs.push(colDef);
          });

          newColumnDefs = orderedColumnDefs;
        }

        // Store the saved filter to apply when grid API becomes available
        if (filter) {
          setSavedFilterModelFromApi(filter);
          setLastSavedFilterModel(filter);
        }
      }
    }
    setColumnDefs(newColumnDefs);
    setShouldShowGrid(true);
  };

  // Set default state when feature flag is enabled
  useEffect(() => {
    if (!isBillingSaveUserGridViewFlagEnabled) {
      setColumnDefs(defaultColumnDefs);
      setShouldShowGrid(true);
    }
  }, [isBillingSaveUserGridViewFlagEnabled, defaultColumnDefs]);

  // Apply saved filters when grid API becomes available
  useEffect(() => {
    if (isBillingSaveUserGridViewFlagEnabled && gridApi && savedFilterModelFromApi) {
      applyFiltersToGrid(gridApi, savedFilterModelFromApi);
      setShouldShowClearFilters(true);
      setSavedFilterModelFromApi(null); // Clear after applying to avoid re-applying
    }
  }, [isBillingSaveUserGridViewFlagEnabled, gridApi, savedFilterModelFromApi]);

  // Load saved grid view (skip if feature flag is enabled)
  useGetBillingUserGridViewQuery({
    variables: {
      gridName,
    },

    onCompleted: handleGetBillingUserGridViewCompleted,
    onError: () => {
      toast.error("Failed to load grid view");
      setColumnDefs(defaultColumnDefs);
      setShouldShowGrid(true);
    },
    fetchPolicy: "no-cache",
    skip: !isBillingSaveUserGridViewFlagEnabled,
  });

  // Save grid view mutation
  const [createBillingUserGridView, { loading: saveViewLoading }] =
    useCreateBillingUserGridViewMutation({
      onCompleted: () => {
        setShouldShowSaveViewButton(false);
        // Update the last saved filter model to the current state
        const currentFilterModel = gridApi?.getFilterModel();
        setLastSavedFilterModel(currentFilterModel || null);
      },
      onError: () => {
        toast.error("Failed to save grid view");
      },
    });

  // Save current view
  const handleSavedViewClicked = useCallback(async () => {
    const currentGridState = gridApi?.getState();
    if (currentGridState) {
      const { columnVisibility, columnSizing, columnOrder } = currentGridState;
      const filter = currentGridState.filter?.filterModel;
      const configuration = { columnVisibility, columnSizing, columnOrder, filter };
      await createBillingUserGridView({
        variables: {
          input: { gridName, configuration },
        },
      });
    }
  }, [gridApi, createBillingUserGridView, gridName]);

  const handleClearFiltersClicked = () => {
    if (!gridApi) return;

    setShouldShowClearFilters(false);
    gridApi.setFilterModel(null);
    const hasFiltersChanged = !isEqual(null, lastSavedFilterModel);
    setShouldShowSaveViewButton(hasFiltersChanged);
  };

  // Grid event handlers
  const handleOnColumnMoved = (event: AgGrid.ColumnMovedEvent) => {
    if (event.source === "uiColumnMoved") {
      setShouldShowSaveViewButton(true);
    }
  };

  const handleOnColumnVisible = (event: AgGrid.ColumnVisibleEvent) => {
    if (event.source === "toolPanelUi") {
      setShouldShowSaveViewButton(true);
    }
  };

  const handleOnColumnResized = (event: AgGrid.ColumnResizedEvent) => {
    if (event.source === "uiColumnResized") {
      setShouldShowSaveViewButton(true);
    }
  };

  const handleOnFilterChanged = (event: AgGrid.FilterChangedEvent) => {
    if (event.source === "columnFilter") {
      const currentFilterModel = event.api.getFilterModel();
      const hasFiltersChanged = !isEqual(currentFilterModel, lastSavedFilterModel);
      setShouldShowSaveViewButton(hasFiltersChanged);
      setShouldShowClearFilters(true);
    }
  };

  return {
    columnDefs,
    shouldShowSaveViewButton,
    shouldShowClearFilters,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    handleClearFiltersClicked,
    handleOnColumnMoved,
    handleOnColumnVisible,
    handleOnColumnResized,
    handleOnFilterChanged,
  };
};

export type {
  UseGridViewManagerProps,
  UseGridViewManagerReturn,
  ColumnDefs,
  SavableGridConfiguration,
};
